// Smart Sash Admin Panel - RFID Service
// RFID tag registration and management

import { axiosClient } from '../http/axiosClient';
import {
  ApiPaginatedData,
  RegisteredRFIDTag,
  GrantedRFIDTag,
  RegisterRFIDRequest,
  UnregisterRFIDRequest,
  GrantRFIDRequest,
  RevokeRFIDRequest,
  RFIDTagStatus,
  CreateCardRequest,
  RfidTagListItem,
  RfidTagsQueryParams,
  SetCardActiveResult,
  PaginatedResponse,
  PaginationParams,
  toPaginatedResponse,
} from '@/models';

const REGISTERED_PATH = '/rfid/registered';
const GRANTED_PATH = '/rfid/granted';
const TAGS_PATH = '/rfid/tags';
const CARDS_PATH = '/rfid/cards';

/**
 * RFID Service
 */
export const rfidService = {
  /**
   * Get all registered RFID tags
   */
  getAllRegistered: async (params?: PaginationParams): Promise<PaginatedResponse<RegisteredRFIDTag>> => {
    const response = await axiosClient.get<PaginatedResponse<RegisteredRFIDTag>>(
      REGISTERED_PATH,
      { params }
    );
    return response.data;
  },

  /**
   * Get registered tags by device ID
   */
  getRegisteredByDevice: async (deviceId: string): Promise<RegisteredRFIDTag[]> => {
    const response = await axiosClient.get<RegisteredRFIDTag[]>(
      `${REGISTERED_PATH}/device/${deviceId}`
    );
    return response.data;
  },

  /**
   * Register RFID tag to device
   */
  registerTag: async (request: RegisterRFIDRequest): Promise<RegisteredRFIDTag> => {
    const response = await axiosClient.post<RegisteredRFIDTag>(
      REGISTERED_PATH,
      request
    );
    return response.data;
  },

  /**
   * Unregister RFID tag from device
   */
  unregisterTag: async (request: UnregisterRFIDRequest): Promise<void> => {
    await axiosClient.post(`${REGISTERED_PATH}/unregister`, request);
  },

  /**
   * Get all granted RFID tags
   */
  getAllGranted: async (params?: PaginationParams): Promise<PaginatedResponse<GrantedRFIDTag>> => {
    const response = await axiosClient.get<PaginatedResponse<GrantedRFIDTag>>(
      GRANTED_PATH,
      { params }
    );
    return response.data;
  },

  /**
   * Get granted tags by user ID
   */
  getGrantedByUser: async (userId: string): Promise<GrantedRFIDTag[]> => {
    const response = await axiosClient.get<GrantedRFIDTag[]>(
      `${GRANTED_PATH}/user/${userId}`
    );
    return response.data;
  },

  /**
   * Grant RFID tag to user
   */
  grantTag: async (request: GrantRFIDRequest): Promise<GrantedRFIDTag> => {
    const response = await axiosClient.post<GrantedRFIDTag>(GRANTED_PATH, request);
    return response.data;
  },

  /**
   * Revoke RFID tag from user
   */
  revokeTag: async (request: RevokeRFIDRequest): Promise<void> => {
    await axiosClient.post(`${GRANTED_PATH}/revoke`, request);
  },

  /**
   * Get RFID tag status
   */
  getTagStatus: async (rfidTag: string): Promise<RFIDTagStatus> => {
    const response = await axiosClient.get<RFIDTagStatus>(
      `/rfid/status/${rfidTag}`
    );
    return response.data;
  },

  /**
   * Validate RFID tag access
   */
  validateAccess: async (deviceId: string, rfidTag: string): Promise<{ authorized: boolean; userId?: string }> => {
    const response = await axiosClient.post<{ authorized: boolean; userId?: string }>(
      '/rfid/validate',
      { deviceId, rfidTag }
    );
    return response.data;
  },

  /**
   * List RFID tags (admin) with optional filters and pagination.
   * Backed by GET /api/rfid/tags. Requires the `Rfid.ViewAll` permission.
   */
  getTags: async (
    query: RfidTagsQueryParams = {}
  ): Promise<PaginatedResponse<RfidTagListItem>> => {
    const params: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params[key] = value as string | number | boolean;
    }
    const response = await axiosClient.get<ApiPaginatedData<RfidTagListItem>>(TAGS_PATH, {
      params,
    });
    return toPaginatedResponse(response.data);
  },

  /**
   * Create a new RFID card. Requires the `Rfid.CreateCard` permission.
   * Returns the created card payload (shape mirrors the server's response data).
   */
  createCard: async (request: CreateCardRequest): Promise<RfidTagListItem> => {
    const response = await axiosClient.post<RfidTagListItem>('/rfid/create-card', request);
    return response.data;
  },

  /**
   * Toggle an RFID card's active state. Deactivating also revokes any active
   * assignments server-side; reactivating does not restore them.
   * Requires the `Rfid.CreateCard` permission.
   */
  setCardActive: async (cardId: string, isActive: boolean): Promise<SetCardActiveResult> => {
    const response = await axiosClient.patch<SetCardActiveResult>(
      `${CARDS_PATH}/${cardId}/active`,
      { isActive }
    );
    return response.data;
  },
};

export default rfidService;
